// ═══════════════════════════════════════════════════════════════
// Toast — Système de notifications global
// Remplace les alert() natifs par des notifications élégantes
// Session 15 — UX Polish
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';

// ── Global toast emitter (utilisable sans context) ──────────
let toastListeners = [];
const emitToast = (toast) => toastListeners.forEach(l => l(toast));

/**
 * Affiche un toast global.
 * @param {string} message
 * @param {'success'|'error'|'warning'|'info'} type
 * @param {number} duration — ms (0 = permanent)
 */
export const toast = (message, type = 'info', duration = 3500) => {
  emitToast({ id: Date.now() + Math.random(), message, type, duration });
};

toast.success = (msg, duration) => toast(msg, 'success', duration);
toast.error = (msg, duration) => toast(msg, 'error', duration || 5000);
toast.warning = (msg, duration) => toast(msg, 'warning', duration || 4000);
toast.info = (msg, duration) => toast(msg, 'info', duration);

// ── Confirm modal (promesse) ────────────────────────────────
let confirmResolver = null;
let confirmEmitter = null;

export const confirm = (message, { title = 'Confirmation', confirmText = 'Confirmer', cancelText = 'Annuler', danger = false } = {}) => {
  return new Promise((resolve) => {
    confirmResolver = resolve;
    if (confirmEmitter) confirmEmitter({ message, title, confirmText, cancelText, danger });
  });
};

// ── Toast Container (monter une fois dans App) ──────────────
const ICONS = {
  success: <CheckCircle size={16} />,
  error: <XCircle size={16} />,
  warning: <AlertTriangle size={16} />,
  info: <Info size={16} />,
};

const COLORS = {
  success: { bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)', text: '#10b981' },
  error:   { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
  warning: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', text: '#f59e0b' },
  info:    { bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)', text: '#3b82f6' },
};

const ToastItem = ({ toast: t, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const c = COLORS[t.type] || COLORS.info;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    if (t.duration > 0) {
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => onDismiss(t.id), 300);
      }, t.duration);
      return () => clearTimeout(timer);
    }
  }, [t, onDismiss]);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '12px 16px', borderRadius: '10px',
      background: c.bg, border: `1px solid ${c.border}`,
      backdropFilter: 'blur(12px)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      color: c.text, fontSize: '13px', fontWeight: '500',
      maxWidth: '420px', minWidth: '280px',
      transform: visible ? 'translateX(0)' : 'translateX(120%)',
      opacity: visible ? 1 : 0,
      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    }}>
      {ICONS[t.type]}
      <span style={{ flex: 1, color: 'var(--text)' }}>{t.message}</span>
      <button onClick={() => { setVisible(false); setTimeout(() => onDismiss(t.id), 300); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)', display: 'flex' }}>
        <X size={14} />
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  const [confirmData, setConfirmData] = useState(null);

  useEffect(() => {
    const handler = (t) => setToasts(prev => [...prev.slice(-4), t]); // max 5
    toastListeners.push(handler);
    confirmEmitter = (data) => setConfirmData(data);
    return () => {
      toastListeners = toastListeners.filter(l => l !== handler);
      confirmEmitter = null;
    };
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const handleConfirm = (result) => {
    setConfirmData(null);
    if (confirmResolver) { confirmResolver(result); confirmResolver = null; }
  };

  return (
    <>
      {/* Toast stack */}
      <div style={{
        position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: '8px',
        pointerEvents: 'none',
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{ pointerEvents: 'auto' }}>
            <ToastItem toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </div>

      {/* Confirm modal */}
      {confirmData && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000,
          backdropFilter: 'blur(4px)',
        }} onClick={() => handleConfirm(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            // S51 — utiliser --card-bg/--card-border qui sont définis pour les
            // deux thèmes (jour + nuit) ; --modal-bg n'était pas hérité partout
            // et le fallback #1a1a2e gardait la modale sombre en mode jour.
            backgroundColor: 'var(--card-bg, #1a1a2e)',
            border: '1px solid var(--card-border, #2a2a4a)',
            borderRadius: '14px', padding: '28px', width: '400px', maxWidth: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'fadeInScale 0.2s ease-out',
            color: 'var(--text, #e5e5ed)',
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text, #e5e5ed)', marginBottom: '12px' }}>
              {confirmData.title}
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary, #8888a0)', lineHeight: '1.5', marginBottom: '24px' }}>
              {confirmData.message}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => handleConfirm(false)} style={{
                padding: '8px 18px', border: '1px solid var(--card-border)',
                borderRadius: '8px', background: 'transparent',
                color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer',
              }}>
                {confirmData.cancelText}
              </button>
              <button onClick={() => handleConfirm(true)} style={{
                padding: '8px 18px', border: 'none', borderRadius: '8px',
                background: confirmData.danger ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              }}>
                {confirmData.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ToastContainer;
